import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RiEditLine } from '@remixicon/react';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import DistanceField, {
  type DistanceDraft,
  distanceDraftOf,
  distanceError,
  typedDistance,
} from '@/pages/warehouse/components/DistanceField';
import OutboundDestinationStep, {
  type DestinationDraft,
  type DestinationField,
} from '@/pages/warehouse/components/OutboundDestinationStep';
import { Shipment, shipmentsAPI } from '@/services/api/shipments';

/** Statuses whose recipient + destination ops may still edit (pre-dispatch). */
const EDITABLE_STATUSES = ['CREATED', 'AT_HUB'];

/**
 * "Ubah" shown for a shipment still before dispatch. DIRECT_4W keeps its
 * destination on the route plan. The server has the final word (items with a
 * rider or a delivery-service delivery → 409 SHIPMENT_NOT_EDITABLE).
 */
export const canEditShipmentDestination = (
  s: Pick<Shipment, 'status' | 'type'>
): boolean => EDITABLE_STATUSES.includes(s.status) && s.type !== 'DIRECT_4W';

interface Props {
  /** The shipment to edit; null = closed. */
  shipment: Shipment | null;
  onClose: () => void;
  onSaved: (updated: Shipment) => void;
}

const draftOf = (s: Shipment): DestinationDraft => ({
  recipientName: s.receiverName ?? '',
  recipientPhone: s.receiverPhone ?? '',
  address: s.destinationAddress ?? '',
  latitude: String(s.destinationLat ?? ''),
  longitude: String(s.destinationLong ?? ''),
  destNotes: s.destinationNotes ?? '',
  manual: true,
  matched: null,
  // Shipments carry no WMS city: the step hides the field (addressOnly).
  city: null,
  cityTouched: false,
  cityWarning: null,
});

const coord = (raw: string): number | null => {
  const t = raw.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Required-input guard; the server validates again. */
const localErrors = (
  d: DestinationDraft
): Partial<Record<DestinationField, string>> => {
  const out: Partial<Record<DestinationField, string>> = {};
  if (!d.recipientName.trim()) out.recipientName = 'Nama penerima wajib diisi.';
  if (!d.recipientPhone.trim()) out.recipientPhone = 'Telepon wajib diisi.';
  if (!d.address.trim()) out.address = 'Alamat tujuan wajib diisi.';
  const lat = coord(d.latitude);
  const lng = coord(d.longitude);
  if (lat === null || Math.abs(lat) > 90)
    out.latitude = 'Latitude harus angka -90…90.';
  if (lng === null || Math.abs(lng) > 180)
    out.longitude = 'Longitude harus angka -180…180.';
  return out;
};

/**
 * Shipments "Ubah": penerima & alamat before dispatch. Same input as the WMS
 * outbound form (typed address, Google suggestions, geocode, map pin). The
 * BE rewrites the items (address, coordinates, H3), moves the stop in any
 * open Dispatch preview and the WMS order that shipped it.
 */
const EditShipmentDestinationModal: React.FC<Props> = ({
  shipment,
  onClose,
  onSaved,
}) => {
  const initial = useMemo(
    () => (shipment ? draftOf(shipment) : null),
    [shipment]
  );
  const [draft, setDraft] = useState<DestinationDraft | null>(initial);
  const [errors, setErrors] = useState<
    Partial<Record<DestinationField, string>>
  >({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [distance, setDistance] = useState<DistanceDraft>(() =>
    distanceDraftOf(shipment?.distance)
  );

  useEffect(() => {
    setDraft(initial);
    setDistance(distanceDraftOf(shipment?.distance));
    setErrors({});
    setFormError(null);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const patch = useCallback(
    (p: Partial<DestinationDraft>) =>
      setDraft((d) => (d ? { ...d, ...p } : d)),
    []
  );
  const fieldError = useCallback(
    (key: DestinationField) => errors[key] ?? null,
    [errors]
  );

  const close = () => {
    if (!submitting) onClose();
  };

  const moved =
    !!draft &&
    !!initial &&
    (draft.latitude !== initial.latitude ||
      draft.longitude !== initial.longitude);
  // Only a shipment that carries a distance gets one re-estimated.
  const autoDistance = moved && shipment?.distance != null;
  const shownDistance =
    moved && !distance.touched ? { value: '', touched: false } : distance;
  const distanceProblem = distanceError(distance);

  const submit = async () => {
    if (!shipment || !draft) return;
    const local = localErrors(draft);
    setErrors(local);
    setFormError(null);
    if (Object.keys(local).length || distanceProblem) return;
    setSubmitting(true);
    try {
      const updated = await shipmentsAPI.updateDestination(shipment.id, {
        receiverName: draft.recipientName.trim(),
        receiverPhone: draft.recipientPhone.trim(),
        address: draft.address.trim(),
        latitude: coord(draft.latitude) as number,
        longitude: coord(draft.longitude) as number,
        notes: draft.destNotes.trim(),
        distance: typedDistance(distance),
      });
      toast.success('Penerima & alamat diubah', {
        description: `${updated.waybill} · Dispatch & label ikut alamat baru — cetak ulang label bila sudah tercetak.`,
      });
      onSaved(updated);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Gagal menyimpan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={shipment !== null}
      onClose={close}
      icon={<RiEditLine />}
      title="Ubah penerima & alamat"
      subtitle={
        shipment ? `${shipment.waybill} · ${shipment.bookingId}` : undefined
      }
      size="lg"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={close}
            disabled={submitting}
          >
            Batal
          </Button>
          <Button
            onClick={() => void submit()}
            loading={submitting}
            data-testid="shipment-edit-destination-submit"
          >
            Simpan
          </Button>
        </div>
      }
    >
      {shipment && draft && (
        <div
          className="flex flex-col gap-4"
          data-testid="shipment-edit-destination-dialog"
        >
          <p className="text-sm text-text-sub-600">
            Bisa diubah selama shipment belum di-dispatch. Titik (H3) dihitung
            ulang dan stop di preview dispatch ikut pindah.
          </p>
          {formError && (
            <Alert
              status="error"
              appearance="stroke"
              size="lg"
              title="Gagal menyimpan"
              data-testid="shipment-edit-destination-error"
            >
              {formError}
            </Alert>
          )}
          <OutboundDestinationStep
            client={null}
            transfer={false}
            addressOnly
            value={draft}
            onChange={patch}
            fieldError={fieldError}
          />
          <DistanceField
            value={shownDistance}
            auto={autoDistance}
            onChange={setDistance}
            error={distanceProblem}
            testId="shipment-edit-distance"
          />
        </div>
      )}
    </FormModal>
  );
};

export default EditShipmentDestinationModal;
